import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Botao, Selo, classesBotao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Entrada, Selecao } from '../../componentes/formulario';
import { get } from '../../nucleo/api';
import { data as fmtData, email as fmtEmail, hora, textoLivre } from '../../nucleo/formato';
import { useDebounce, useEstadoCanais, useIntervaloConversas } from './dados';
import { useSessao } from '../../nucleo/sessao';
import { useDiscreto } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { caixaDaUrl, CHAVE_CAIXAS_EMAIL, CHAVE_ETIQUETAS, iniciais, nomeDoContato, PRIORIDADES, type Canal, type Conversa, type EstadoCaixas, type PaginaApi } from './tipos';
import { SelosConversa } from './PainelConversa';
import { carregarMais } from '../../nucleo/foco';

function quando(v: string | null) {
  if (!v) return '';
  const d = new Date(v);
  return d.toDateString() === new Date().toDateString() ? hora(d) : fmtData(d);
}

/** E-mail: a prévia guardada é "assunto: texto"; o assunto já tem linha própria, então sai da prévia. */
export function resumoDaLista(c: Pick<Conversa, 'canal' | 'assunto' | 'ultima_mensagem_resumo'>): string | null {
  const r = c.ultima_mensagem_resumo;
  if (!r || c.canal !== 'email' || !c.assunto) return r;
  if (r === c.assunto) return null;
  return r.startsWith(`${c.assunto}: `) ? r.slice(c.assunto.length + 2) : r;
}

function Filtro({ ativo, aoClicar, children }: { ativo: boolean; aoClicar: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={aoClicar}
      className={`rounded-full border-2 px-3 py-1 text-xs font-medium ${ativo ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:text-tinta-1'}`}
    >
      {children}
    </button>
  );
}

/** Lista à esquerda da caixa unificada, filtrada pelo canal da aba. */
export type EstadoLista = 'carregando' | 'erro' | 'ok';

export function ListaConversas({ canal, selecionada, aoEscolher, aoEstado }: { canal: Canal; selecionada: string | null; aoEscolher: (c: Conversa) => void; aoEstado?: (e: EstadoLista) => void }) {
  const { t } = useTranslation('conversas');
  useDiscreto(); // Review r63 (VIS-M1): o endereço da caixa passa pelo `email()` e redesenha quando o discreto muda.
  const { t: tn } = useTranslation();
  const s = useSessao();
  const canais = useEstadoCanais();
  const [encerradas, setEncerradas] = useState(false);
  const [minhas, setMinhas] = useState(false);
  const [naoLidas, setNaoLidas] = useState(false);
  const [busca, setBusca] = useState('');
  // Trilha 5b (Digisac): filtro por etiqueta e prioridade.
  const [etiqueta, setEtiqueta] = useState('');
  const [prioridade, setPrioridade] = useState('');
  // Review r46 robustez (M4): chave fora de ['conversas'], para o sinal do tempo real (que invalida ['conversas']) não refazer a
  // contagem a cada mensagem; classificar uma conversa invalida esta chave (`useAcao`).
  const etiquetas = useQuery({ queryKey: CHAVE_ETIQUETAS, queryFn: () => get<{ itens: { nome: string; conversas: number }[] }>('/conversas/etiquetas'), staleTime: 30_000 });
  // U11 (lote 1): na aba E-mail, "Todas as caixas" ou uma caixa, guardada na URL (`?caixa=`), junto da conversa aberta.
  const [params, setParams] = useSearchParams();
  const caixas = useQuery({ queryKey: CHAVE_CAIXAS_EMAIL, queryFn: () => get<EstadoCaixas>('/conversas/email/caixas'), staleTime: 30_000, enabled: canal === 'email' });
  // U11 (lote 2): só as caixas que a pessoa lê (a pessoal alheia vem só para quem configura saber de quem é).
  const enderecos = (caixas.data?.caixas ?? []).filter((c) => c.le !== false).map((c) => c.endereco);
  // Review r56 (ROB-B3 = SPEC-B3): `?caixa=` saneado; inválido ou fora das monitoradas é ignorado ("Todas as caixas").
  const caixa = canal === 'email' ? caixaDaUrl(params.get('caixa'), caixas.data ? enderecos : null) : '';
  const opcoesCaixa = enderecos;
  function escolherCaixa(v: string) {
    setParams(
      (atual) => {
        const n = new URLSearchParams(atual);
        if (v) n.set('caixa', v);
        else n.delete('caixa');
        return n;
      },
      { replace: true },
    );
  }
  const buscaD = useDebounce(busca);
  const intervalo = useIntervaloConversas(8000);
  // Páginas de 100 com "Carregar mais": nada de cortar a lista em silêncio (as páginas já carregadas se atualizam juntas no sinal
  // de tempo real, ou a cada 8 s sem ele).
  const q = useInfiniteQuery({
    queryKey: ['conversas', 'lista', canal, encerradas, minhas, naoLidas, buscaD, etiqueta, prioridade, caixa],
    queryFn: ({ pageParam }) =>
      get<PaginaApi<Conversa>>('/conversas/conversas', {
        canal,
        estado: encerradas ? 'encerrada' : 'ativas',
        minhas: minhas || undefined,
        nao_lidas: naoLidas || undefined,
        busca: buscaD || undefined,
        etiqueta: etiqueta || undefined,
        prioridade: prioridade || undefined,
        caixa: caixa || undefined,
        limite: 100,
        cursor: pageParam || undefined,
      }),
    initialPageParam: '',
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
    refetchInterval: intervalo,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  const estadoLista: EstadoLista = q.isError ? 'erro' : q.data ? 'ok' : 'carregando';
  useEffect(() => {
    aoEstado?.(estadoLista);
  }, [estadoLista, aoEstado]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 border-b border-borda p-3">
        {/* U15 F2: alvo da dica "aqui você procura" (a busca das conversas). */}
        <div className="relative" data-tour="filtro">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <Entrada aria-label={t('filtros.buscar')} placeholder={t('filtros.buscar')} className="pl-9" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Filtro ativo={!encerradas} aoClicar={() => setEncerradas(false)}>
            {t('filtros.ativas')}
          </Filtro>
          <Filtro ativo={encerradas} aoClicar={() => setEncerradas(true)}>
            {t('filtros.encerradas')}
          </Filtro>
          <Filtro ativo={minhas} aoClicar={() => setMinhas((v) => !v)}>
            {t('filtros.minhas')}
          </Filtro>
          <Filtro ativo={naoLidas} aoClicar={() => setNaoLidas((v) => !v)}>
            {t('filtros.naoLidas')}
          </Filtro>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-2">
          <Selecao
            aria-label={t('t5b.etiquetas')}
            opcoes={[{ valor: '', rotulo: t('t5b.todasEtiquetas') }, ...(etiquetas.data?.itens ?? []).map((e) => ({ valor: e.nome, rotulo: e.nome }))]}
            value={etiqueta}
            onChange={(e) => setEtiqueta(e.target.value)}
          />
          <Selecao aria-label={t('t5b.prioridade')} opcoes={[{ valor: '', rotulo: t('t5b.todasPrioridades') }, ...PRIORIDADES.map((p) => ({ valor: p, rotulo: t(`t5b.prioridades.${p}`) }))]} value={prioridade} onChange={(e) => setPrioridade(e.target.value)} />
          {canal === 'email' && opcoesCaixa.length > 1 && (
            <div className="col-span-2 min-w-0">
              <Selecao aria-label={t('u11.filtroCaixa')} opcoes={[{ valor: '', rotulo: t('u11.todasCaixas') }, ...opcoesCaixa.map((c) => ({ valor: c, rotulo: fmtEmail(c) }))]} value={caixa} onChange={(e) => escolherCaixa(e.target.value)} />
            </div>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : itens.length === 0 ? (
          buscaD ? (
            <Vazio titulo={t('lista.semResultado')} texto={t('lista.semResultadoTexto')} />
          ) : (
            <Vazio
              texto={encerradas ? t('lista.vaziaEncerradas') : canal === 'email' ? t('lista.vaziaEmail') : canal === 'site' ? t('site.listaVazia') : t('lista.vazia')}
              acao={
                // Sem conector, o caminho é conectar (só com permissão); com conector, a ação é ver o outro lado da lista.
                canais.data && !canais.data[canal] && s.instalado('integracoes') && s.pode('integracoes.ver') ? (
                  <Link to="/integracoes/integracoes/conectores" className={classesBotao()}>
                    {t('lista.conectarCanal')}
                  </Link>
                ) : (
                  <Botao onClick={() => setEncerradas((v) => !v)}>{encerradas ? t('lista.verAtivas') : t('lista.verEncerradas')}</Botao>
                )
              }
            />
          )
        ) : (
          <ul className="divide-y divide-borda">
            {itens.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  aria-current={c.id === selecionada}
                  onClick={() => aoEscolher(c)}
                  className={`flex w-full min-w-0 items-start gap-3 border-l-2 px-3 py-3 text-left hover:bg-sup-2 ${c.id === selecionada ? 'border-acento-forte bg-sup-2' : 'border-transparent'}`}
                >
                  <span aria-hidden className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-borda bg-sup-3 text-xs font-semibold text-tinta-2">
                    {iniciais(c.parceiro_nome ?? '')}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex min-w-0 items-baseline justify-between gap-2">
                      <span className={`truncate text-sm ${c.nao_lidas ? 'font-semibold text-tinta-1' : 'font-medium text-tinta-1'}`} title={nomeDoContato(c)}>{nomeDoContato(c)}</span>
                      <span className="numero shrink-0 text-xs text-tinta-3">{quando(c.ultima_mensagem_em ?? c.created_at)}</span>
                    </span>
                    {c.canal === 'email' && (
                      <TextoCortado className={`text-sm ${c.nao_lidas ? 'font-medium text-tinta-1' : 'text-tinta-2'}`}>{c.assunto ? textoLivre(c.assunto, c.parceiro_nome, c.remetente_nome) : t('w9.semAssunto')}</TextoCortado>
                    )}
                    <span className="flex min-w-0 items-center justify-between gap-2">
                      <span className={`truncate text-sm ${c.nao_lidas ? 'text-tinta-1' : 'text-tinta-3'}`} title={resumoDaLista(c) ? textoLivre(resumoDaLista(c), c.parceiro_nome, c.remetente_nome) : undefined}>{resumoDaLista(c) ? textoLivre(resumoDaLista(c), c.parceiro_nome, c.remetente_nome) : c.canal === 'email' && c.assunto ? '' : t('lista.semMensagens')}</span>
                      {c.nao_lidas > 0 && (
                        <span aria-label={`${c.nao_lidas} ${t('filtros.naoLidas')}`} className="numero flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento px-1.5 text-xs font-semibold text-acento-tinta">
                          {c.nao_lidas}
                        </span>
                      )}
                    </span>
                    <span className="flex min-w-0 items-center gap-2 text-xs text-tinta-3">
                      {c.estado === 'pendente' && <Selo cor="laranja">{t('estados.pendente')}</Selo>}
                      {c.estado === 'encerrada' && <Selo>{t('estados.encerrada')}</Selo>}
                      {c.atendente_nome && <span className="truncate" title={c.atendente_nome}>{c.atendente_nome}</span>}
                      {c.fila_nome && <Selo>{c.fila_nome}</Selo>}
                    </span>
                    {c.canal === 'email' && c.caixa && (
                      <span className="min-w-0" title={fmtEmail(c.caixa)} data-caixa-conversa>
                        <Selo cor="ciano" quebra>
                          {fmtEmail(c.caixa)}
                        </Selo>
                      </span>
                    )}
                    <SelosConversa conversa={c} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {q.hasNextPage && (
          <div className="flex justify-center p-3">
            <Botao tamanho="sm" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
              {tn('comum.carregarMais')}
            </Botao>
          </div>
        )}
      </div>
    </div>
  );
}
